import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  adminPaymentLinkService,
  type AdminPaymentLink,
} from '../services/adminPaymentLinkService';

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non défini';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Non défini';
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const STATUS_LABELS: Record<AdminPaymentLink['sessionStatus'], string> = {
  initiated: 'Lien prêt',
  pending: 'Paiement en attente',
  paid: 'Paiement confirmé',
  failed: 'Paiement à relancer',
  canceled: 'Paiement annulé',
  expired: 'Lien expiré',
};

const STATUS_BADGES: Record<AdminPaymentLink['sessionStatus'], string> = {
  initiated: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-700',
  expired: 'bg-orange-100 text-orange-700',
};

const QuoteCheckout = () => {
  const { paymentLinkKey = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [paymentLink, setPaymentLink] = useState<AdminPaymentLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const loadPaymentLink = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const nextPaymentLink = await adminPaymentLinkService.getPublicPaymentLink(
          paymentLinkKey
        );
        setPaymentLink(nextPaymentLink);
      } catch (error) {
        console.error(error);
        setErrorMessage(
          error instanceof Error ? error.message : 'Impossible de charger ce devis.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (!paymentLinkKey) {
      setErrorMessage('Lien de devis invalide.');
      setIsLoading(false);
      return;
    }

    loadPaymentLink().catch((error) => {
      console.error(error);
      setErrorMessage('Impossible de charger ce devis.');
      setIsLoading(false);
    });
  }, [paymentLinkKey]);

  const clientMessage =
    paymentLink?.metadata && typeof paymentLink.metadata.clientMessage === 'string'
      ? paymentLink.metadata.clientMessage
      : '';

  const lineItems =
    paymentLink && paymentLink.lineItems.length > 0
      ? paymentLink.lineItems
      : paymentLink
        ? [
            {
              id: 'item_1',
              label: paymentLink.serviceName || 'Service',
              quantity: 1,
              unitAmount: paymentLink.amount,
              lineTotal: paymentLink.amount,
              currencyCode: paymentLink.currencyCode,
            },
          ]
        : [];

  const quoteTotal = paymentLink ? Number(paymentLink.amount || 0) : 0;
  const isRecurring = paymentLink?.paymentMode === 'recurring';
  const recurringAmount = paymentLink ? Number(paymentLink.recurringAmount || paymentLink.amount || 0) : 0;
  const invoicePdfUrl = paymentLink
    ? adminPaymentLinkService.getInvoicePdfUrl(paymentLink.paymentLinkKey)
    : '';

  const isExpired = (() => {
    if (!paymentLink?.quoteValidUntil) {
      return false;
    }

    const date = new Date(paymentLink.quoteValidUntil);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    return date.getTime() < Date.now();
  })();

  const canPay =
    paymentLink &&
    !isExpired &&
    ['initiated', 'pending', 'failed'].includes(paymentLink.sessionStatus);

  const paymentStatus = (searchParams.get('paymentStatus') || '').toLowerCase();
  const showRetryNotice = paymentStatus === 'canceled' || paymentStatus === 'failed';
  const showPendingNotice = paymentStatus === 'pending';

  return (
    <>
      <Helmet>
        <title>Votre devis - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Consultez votre devis Couteau Suisse Digital et accédez à votre lien de paiement sécurisé."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center text-gray-600">
              Chargement du devis...
            </div>
          ) : errorMessage || !paymentLink ? (
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Devis introuvable</h1>
              <p className="text-gray-600 mb-8">{errorMessage || 'Le lien demandé est invalide.'}</p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-neon-orange px-8 py-4 font-semibold text-white hover:bg-[#FF4500] transition-colors"
              >
                Contacter notre équipe
              </Link>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <section className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-b from-[#f7f3ef] to-[#fffaf7] px-8 py-10 border-b border-[#f1ece8]">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div>
                      <div className="text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
                        Devis Couteau Suisse Digital
                      </div>
                      <h1 className="text-4xl font-bold text-gray-900 mt-3">
                        {paymentLink.quoteNumber}
                      </h1>
                      <p className="text-lg text-gray-600 mt-4 max-w-2xl">
                        Bonjour {paymentLink.clientName}, voici votre devis pour{' '}
                        <strong>{paymentLink.serviceName}</strong>
                        {lineItems.length > 1 ? ` incluant ${lineItems.length} produits.` : '.'}
                        {isRecurring
                          ? ' Ce paiement active un abonnement récurrent.'
                          : ''}
                      </p>
                    </div>

                    <div
                      className={`inline-flex self-start rounded-full px-4 py-2 text-sm font-semibold ${STATUS_BADGES[paymentLink.sessionStatus]}`}
                    >
                      {STATUS_LABELS[paymentLink.sessionStatus]}
                    </div>
                  </div>
                </div>

                <div className="px-8 py-8 grid gap-8 lg:grid-cols-[minmax(0,1.3fr)_320px]">
                  <div className="space-y-6">
                    {showRetryNotice && (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
                        <h2 className="text-xl font-semibold text-amber-900">
                          Paiement annule ou echoue
                        </h2>
                        <p className="mt-2 text-sm text-amber-800">
                          Aucun souci, tu peux relancer le paiement quand tu veux avec le bouton
                          de paiement securise.
                        </p>
                      </div>
                    )}

                    {showPendingNotice && (
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
                        <h2 className="text-xl font-semibold text-blue-900">
                          Paiement en attente de confirmation
                        </h2>
                        <p className="mt-2 text-sm text-blue-800">
                          Le lien est actif. Tu peux patienter quelques instants ou relancer le
                          paiement si besoin.
                        </p>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">
                        Récapitulatif
                      </h2>
                      <div className="space-y-3 text-sm text-gray-700">
                        <div className="flex justify-between gap-4">
                          <span>Client</span>
                          <strong className="text-right">{paymentLink.clientName}</strong>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Email</span>
                          <strong className="text-right">{paymentLink.clientEmail}</strong>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Service / Offre</span>
                          <strong className="text-right">{paymentLink.serviceName}</strong>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Type de paiement</span>
                          <strong className="text-right">
                            {isRecurring ? 'Abonnement récurrent' : 'Paiement unique'}
                          </strong>
                        </div>
                        {isRecurring && (
                          <div className="flex justify-between gap-4">
                            <span>Fréquence</span>
                            <strong className="text-right">
                              {paymentLink.recurringInterval || 'Mensuel'}
                            </strong>
                          </div>
                        )}
                        {isRecurring && (
                          <div className="flex justify-between gap-4">
                            <span>Montant récurrent</span>
                            <strong className="text-right">
                              {recurringAmount.toFixed(2)} {paymentLink.currencyCode}
                            </strong>
                          </div>
                        )}
                        <div className="pt-2 border-t border-gray-200">
                          <div className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-2">
                            Détail produits
                          </div>
                          <div className="space-y-2">
                            {lineItems.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between gap-4"
                              >
                                <div className="text-right">
                                  <div className="font-medium text-gray-900">{item.label}</div>
                                  <div className="text-xs text-gray-500">
                                    Qté {item.quantity} • {item.unitAmount.toFixed(2)}{' '}
                                    {item.currencyCode}
                                  </div>
                                </div>
                                <strong className="text-right">
                                  {item.lineTotal.toFixed(2)} {item.currencyCode}
                                </strong>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Date du devis</span>
                          <strong className="text-right">{formatDate(paymentLink.createdAt)}</strong>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span>Validité</span>
                          <strong className="text-right">
                            {formatDate(paymentLink.quoteValidUntil)}
                          </strong>
                        </div>
                        <div className="flex justify-between gap-4 text-base">
                          <span>Total</span>
                          <strong className="text-right text-neon-orange">
                            {quoteTotal.toFixed(2)} {paymentLink.currencyCode}
                          </strong>
                        </div>
                      </div>
                    </div>

                    {clientMessage && (
                      <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-3">
                          Message complémentaire
                        </h2>
                        <p className="text-gray-600 leading-7 whitespace-pre-wrap">
                          {clientMessage}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-5">
                    <div className="bg-[#fff4eb] rounded-2xl border border-[#f6d8c0] p-6">
                      <div className="text-sm font-semibold text-[#7c4a22] uppercase tracking-wide">
                        Paiement sécurisé
                      </div>
                      <div className="text-3xl font-bold text-gray-900 mt-3">
                        {quoteTotal.toFixed(2)} {paymentLink.currencyCode}
                      </div>
                      <p className="text-sm text-gray-600 mt-4">
                        {isRecurring
                          ? 'Le premier paiement active votre mandat récurrent sécurisé sur Mollie.'
                          : 'Le paiement est traité sur l’environnement sécurisé Mollie.'}
                      </p>

                      {paymentLink.sessionStatus === 'paid' ? (
                        <div className="mt-6 space-y-3">
                          <div className="rounded-xl bg-emerald-100 px-4 py-3 text-sm font-medium text-emerald-700">
                            Ce devis a déjà été payé.
                          </div>
                          {invoicePdfUrl && (
                            <a
                              href={invoicePdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex w-full items-center justify-center rounded-full border border-emerald-200 bg-white px-6 py-4 font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
                            >
                              Télécharger la facture PDF
                            </a>
                          )}
                        </div>
                      ) : isExpired ? (
                        <div className="mt-6 rounded-xl bg-orange-100 px-4 py-3 text-sm font-medium text-orange-700">
                          La validité du devis est dépassée. Contactez notre équipe pour une mise
                          à jour.
                        </div>
                      ) : (
                        <a
                          href={canPay ? paymentLink.paymentUrl : '/contact'}
                          className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-neon-orange px-6 py-4 font-semibold text-white hover:bg-[#FF4500] transition-colors"
                        >
                          {canPay
                            ? isRecurring
                              ? "Activer l'abonnement sécurisé"
                              : 'Accéder au paiement sécurisé'
                            : 'Contacter notre équipe'}
                        </a>
                      )}
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Besoin d’aide ?</h3>
                      <p className="text-sm text-gray-600 leading-6">
                        Si vous souhaitez ajuster le devis, poser une question ou planifier un
                        échange, notre équipe peut vous répondre rapidement.
                      </p>
                      <Link
                        to="/contact"
                        className="mt-5 inline-flex items-center text-sm font-semibold text-neon-orange hover:text-[#FF4500]"
                      >
                        Contacter le support
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            </motion.div>
          )}
        </div>
      </main>
    </>
  );
};

export default QuoteCheckout;
